import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import inventoryAPI from '../../../api/inventory';
import { masterDataServices } from '../../../api/masterData';

const InventoryManagementTab = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Form state
  const [formData, setFormData] = useState({
    medicineId: '',
    manufacturerId: '',
    batchNumber: '',
    quantity: {
      current: 0,
      reserved: 0,
      available: 0,
      reorderLevel: 0,
      maxLevel: 0
    },
    unit: 'tablets',
    purchase: {
      purchaseDate: new Date().toISOString().split('T')[0],
      supplierName: '',
      invoiceNumber: '',
      unitCost: 0,
      totalCost: 0
    },
    storage: {
      expiryDate: '',
      manufacturingDate: '',
      storageLocation: '',
      conditions: {
        temperature: '',
        humidity: '',
        lightProtection: false
      }
    },
    pricing: {
      sellingPrice: 0,
      mrp: 0
    },
    status: 'active',
    alerts: {
      lowStock: true,
      nearExpiry: true,
      expired: true
    }
  });

  // Unit options based on the swagger schema
  const unitOptions = [
    'tablets', 'capsules', 'bottles', 'vials', 'ampoules',
    'ml', 'mg', 'grams', 'strips', 'pieces'
  ];

  // Status options
  const statusOptions = [
    'active', 'expired', 'recalled', 'damaged', 'quarantine', 'depleted'
  ];

  useEffect(() => {
    fetchInventoryItems();
    fetchMedicines();
    fetchManufacturers();
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    // Calculate available quantity when current or reserved changes
    setFormData(prev => ({
      ...prev,
      quantity: {
        ...prev.quantity,
        available: prev.quantity.current - prev.quantity.reserved
      }
    }));
  }, [formData.quantity.current, formData.quantity.reserved]);

  useEffect(() => {
    // Calculate total cost when unit cost or current quantity changes
    setFormData(prev => ({
      ...prev,
      purchase: {
        ...prev.purchase,
        totalCost: prev.purchase.unitCost * prev.quantity.current
      }
    }));
  }, [formData.purchase.unitCost, formData.quantity.current]);

  const fetchInventoryItems = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };

      const response = await inventoryAPI.getAllInventoryItems(params);
      if (response.success) {
        setInventoryItems(response.data.data?.items || []);
        const pagination = response.data.data?.pagination;
        if (pagination) {
          setTotalPages(Math.ceil(pagination.totalCount / 20));
        }
      } else {
        setError(response.message || 'Failed to fetch inventory items');
        // Set empty items if fetch fails
        setInventoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setError('Failed to fetch inventory items');
      setInventoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await masterDataServices.medicines.getActive();
      setMedicines(response.records || []);
    } catch (error) {
      console.error('Error fetching medicines:', error);
    }
  };

  const fetchManufacturers = async () => {
    try {
      const response = await masterDataServices.manufacturers.getActive();
      setManufacturers(response.records || []);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      medicineId: '',
      manufacturerId: '',
      batchNumber: '',
      quantity: {
        current: 0,
        reserved: 0,
        available: 0,
        reorderLevel: 0,
        maxLevel: 0
      },
      unit: 'tablets',
      purchase: {
        purchaseDate: new Date().toISOString().split('T')[0],
        supplierName: '',
        invoiceNumber: '',
        unitCost: 0,
        totalCost: 0
      },
      storage: {
        expiryDate: '',
        manufacturingDate: '',
        storageLocation: '',
        conditions: {
          temperature: '',
          humidity: '',
          lightProtection: false
        }
      },
      pricing: {
        sellingPrice: 0,
        mrp: 0
      },
      status: 'active',
      alerts: {
        lowStock: true,
        nearExpiry: true,
        expired: true
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === 'number' ? parseFloat(value) || 0 :
      type === 'checkbox' ? checked : value;

    if (name.includes('.')) {
      const nameParts = name.split('.');
      if (nameParts.length === 2) {
        const [parent, child] = nameParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: actualValue
          }
        }));
      } else if (nameParts.length === 3) {
        const [parent, child, grandchild] = nameParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: actualValue
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: actualValue
      }));
    }
  };

  const validateForm = () => {
    if (!formData.medicineId) {
      setError('Please select a medicine');
      return false;
    }
    if (!formData.manufacturerId) {
      setError('Please select a manufacturer');
      return false;
    }
    if (!formData.batchNumber.trim()) {
      setError('Batch number is required');
      return false;
    }
    if (formData.quantity.current <= 0) {
      setError('Current quantity must be greater than 0');
      return false;
    }
    if (!formData.storage.expiryDate) {
      setError('Expiry date is required');
      return false;
    }
    if (formData.purchase.unitCost <= 0) {
      setError('Unit cost must be greater than 0');
      return false;
    }
    if (formData.pricing.sellingPrice <= 0) {
      setError('Selling price must be greater than 0');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      if (editingItem) {
        response = await inventoryAPI.updateInventoryItem(editingItem._id, formData);
      } else {
        response = await inventoryAPI.createInventoryItem(formData);
      }

      if (response.success) {
        setSuccess(editingItem ? 'Inventory item updated successfully' : 'Inventory item created successfully');
        resetForm();
        setShowAddDialog(false);
        setShowEditDialog(false);
        setEditingItem(null);
        fetchInventoryItems();
      } else {
        setError(response.message || 'Failed to save inventory item');
      }
    } catch (error) {
      console.error('Error saving inventory item:', error);
      setError('Failed to save inventory item');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      medicineId: item.medicineId?._id || item.medicineId || '',
      manufacturerId: item.manufacturerId?._id || item.manufacturerId || '',
      batchNumber: item.batchNumber || '',
      quantity: item.quantity || {
        current: 0,
        reserved: 0,
        available: 0,
        reorderLevel: 0,
        maxLevel: 0
      },
      unit: item.unit || 'tablets',
      purchase: {
        purchaseDate: item.purchase?.purchaseDate
          ? new Date(item.purchase.purchaseDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        supplierName: item.purchase?.supplierName || '',
        invoiceNumber: item.purchase?.invoiceNumber || '',
        unitCost: item.purchase?.unitCost || 0,
        totalCost: item.purchase?.totalCost || 0
      },
      storage: {
        expiryDate: item.storage?.expiryDate
          ? new Date(item.storage.expiryDate).toISOString().split('T')[0]
          : '',
        manufacturingDate: item.storage?.manufacturingDate
          ? new Date(item.storage.manufacturingDate).toISOString().split('T')[0]
          : '',
        storageLocation: item.storage?.storageLocation || '',
        conditions: {
          temperature: item.storage?.conditions?.temperature || '',
          humidity: item.storage?.conditions?.humidity || '',
          lightProtection: item.storage?.conditions?.lightProtection || false
        }
      },
      pricing: item.pricing || {
        sellingPrice: 0,
        mrp: 0
      },
      status: item.status || 'active',
      alerts: item.alerts || {
        lowStock: true,
        nearExpiry: true,
        expired: true
      }
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) {
      return;
    }

    try {
      const response = await inventoryAPI.deleteInventoryItem(id);
      if (response.success) {
        setSuccess('Inventory item deleted successfully');
        fetchInventoryItems();
      } else {
        setError(response.message || 'Failed to delete inventory item');
      }
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      setError('Failed to delete inventory item');
    }
  };

  const handleAddNew = () => {
    resetForm();
    setError('');
    setSuccess('');
    setShowAddDialog(true);
  };

  const handleView = (item) => {
    setViewingItem(item);
    setShowViewDialog(true);
  };

  const closeDialogs = () => {
    setShowAddDialog(false);
    setShowEditDialog(false);
    setShowViewDialog(false);
    setEditingItem(null);
    setViewingItem(null);
    setError('');
    setSuccess('');
    resetForm();
  };

  const getMedicineName = (item) => {
    if (typeof item.medicineId === 'object' && item.medicineId?.name) {
      return item.medicineId.name;
    }
    const medicine = medicines.find(m => m._id === item.medicineId);
    return medicine?.name || 'Unknown Medicine';
  };

  const getManufacturerName = (item) => {
    if (typeof item.manufacturerId === 'object' && item.manufacturerId?.name) {
      return item.manufacturerId.name;
    }
    const manufacturer = manufacturers.find(m => m._id === item.manufacturerId);
    return manufacturer?.name || 'Unknown Manufacturer';
  };

  const renderViewDialog = () => {
    if (!viewingItem) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-6 bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90 border-b border-[#2D869C]/20">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Icon name="Eye" size={24} className="mr-3" />
                Inventory Item Details
              </h3>
              <button
                onClick={closeDialogs}
                className="text-white/80 hover:text-white transition-colors p-1 hover:bg-white/20 rounded-lg"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-bold text-[#2D869C] mb-4 flex items-center">
                <Icon name="Info" size={20} className="mr-2" />
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Medicine</label>
                  <p className="text-md font-semibold text-gray-900">{getMedicineName(viewingItem)}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Manufacturer</label>
                  <p className="text-md font-semibold text-gray-900">{getManufacturerName(viewingItem)}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Batch Number</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.batchNumber}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Unit</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.unit}</p>
                </div>
                <div>
                  <label className="block text-md font-semibold text-[#2D869C] mb-2">Status</label>
                  <span className={`inline-flex items-center px-4 py-2 rounded-xl text-md font-semibold shadow-sm ${viewingItem.status === 'active' ? 'bg-[#A4EC90]/20 text-green-800' :
                      viewingItem.status === 'expired' ? 'bg-[#E99797]/20 text-red-800' :
                        viewingItem.status === 'damaged' ? 'bg-[#CCB478]/20 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                    {viewingItem.status?.charAt(0).toUpperCase() + viewingItem.status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quantity Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Quantity Information</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Current</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.quantity?.current || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Reserved</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.quantity?.reserved || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Available</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.quantity?.available || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Reorder Level</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.quantity?.reorderLevel || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Max Level</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.quantity?.maxLevel || 0}</p>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Purchase Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Purchase Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Purchase Date</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.purchase?.purchaseDate ?
                      new Date(viewingItem.purchase.purchaseDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Supplier Name</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.purchase?.supplierName || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Invoice Number</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.purchase?.invoiceNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Unit Cost</label>
                  <p className="text-md font-semibold text-gray-900">₹{viewingItem.purchase?.unitCost || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Total Cost</label>
                  <p className="text-md font-semibold text-gray-900">₹{viewingItem.purchase?.totalCost || 0}</p>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Storage Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Storage Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Manufacturing Date</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.storage?.manufacturingDate ?
                      new Date(viewingItem.storage.manufacturingDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Expiry Date</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.storage?.expiryDate ?
                      new Date(viewingItem.storage.expiryDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Storage Location</label>
                  <p className="text-md font-semibold text-gray-900">{viewingItem.storage?.storageLocation || 'N/A'}</p>
                </div>
              </div>

              {/* Storage Conditions */}
              <div>
                <h5 className="text-sm font-semibold text-gray-800 mb-2">Storage Conditions</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-md font-medium text-gray-700 mb-1">Temperature</label>
                    <p className="text-md font-semibold text-gray-900">{viewingItem.storage?.conditions?.temperature || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700 mb-1">Humidity</label>
                    <p className="text-md font-semibold text-gray-900">{viewingItem.storage?.conditions?.humidity || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-md font-medium text-gray-700 mb-1">Light Protection</label>
                    <p className="text-md font-semibold text-gray-900">
                      {viewingItem.storage?.conditions?.lightProtection ? 'Required' : 'Not Required'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Pricing Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Pricing Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Selling Price</label>
                  <p className="text-md font-semibold text-gray-900">₹{viewingItem.pricing?.sellingPrice || 0}</p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">MRP</label>
                  <p className="text-md font-semibold text-gray-900">₹{viewingItem.pricing?.mrp || 0}</p>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Alert Settings */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Alert Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Low Stock Alert</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.alerts?.lowStock ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Near Expiry Alert</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.alerts?.nearExpiry ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Expired Alert</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.alerts?.expired ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* Timestamps */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">System Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Created At</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.createdAt ?
                      new Date(viewingItem.createdAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-1">Updated At</label>
                  <p className="text-md font-semibold text-gray-900">
                    {viewingItem.updatedAt ?
                      new Date(viewingItem.updatedAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end p-6 border-t border-gray-200">
            <button
              onClick={closeDialogs}
              className="px-6 py-3 text-white bg-[#2D869C] rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] font-semibold shadow-md hover:shadow-lg transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const INP = 'w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] hover:border-[#2D869C] transition-all bg-white';
  const INP_RO = 'w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-400 focus:outline-none bg-gray-50 cursor-not-allowed';
  const LBL = 'block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide';

  const renderFormDialog = (isEdit = false) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Modal Header */}
        <div className="p-6 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] border-b border-[#2D869C]/20">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-white flex items-center">
              <Icon name="Package" size={24} className="mr-3" />
              {isEdit ? 'Edit Inventory Item' : 'Add New Inventory Item'}
            </h3>
            <button
              onClick={closeDialogs}
              className="text-white/80 hover:text-white transition-colors p-1 hover:bg-white/20 rounded-lg"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="border-2 border-red-200 bg-red-50 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 font-semibold text-md">
              <Icon name="AlertCircle" size={18} />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="border-2 border-[#9DC9D4] bg-[#9DC9D4]/20 text-[#2D869C] px-4 py-3 rounded-xl flex items-center gap-3 font-semibold text-md">
              <Icon name="CheckCircle2" size={18} />
              <span>{success}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="Info" size={18} />
                Basic Information
              </h4>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={LBL}>Medicine <span className="text-red-500 normal-case">*</span></label>
                <select name="medicineId" value={formData.medicineId} onChange={handleInputChange} className={INP} required>
                  <option value="">Select Medicine</option>
                  {medicines.map(medicine => (
                    <option key={medicine._id} value={medicine._id}>
                      {medicine.name} - {medicine.drug?.name || medicine.drug}
                      {medicine.manufacturer?.name && ` (${medicine.manufacturer.name})`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LBL}>Manufacturer <span className="text-red-500 normal-case">*</span></label>
                <select name="manufacturerId" value={formData.manufacturerId} onChange={handleInputChange} className={INP} required>
                  <option value="">Select Manufacturer</option>
                  {manufacturers.map(manufacturer => (
                    <option key={manufacturer._id} value={manufacturer._id}>{manufacturer.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LBL}>Batch Number <span className="text-red-500 normal-case">*</span></label>
                <input type="text" name="batchNumber" value={formData.batchNumber} onChange={handleInputChange} className={INP} required />
              </div>
              <div>
                <label className={LBL}>Unit <span className="text-red-500 normal-case">*</span></label>
                <select name="unit" value={formData.unit} onChange={handleInputChange} className={INP} required>
                  {unitOptions.map(unit => (
                    <option key={unit} value={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LBL}>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className={INP}>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quantity Information */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="Package" size={18} />
                Quantity Information
              </h4>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className={LBL}>Current <span className="text-red-500 normal-case">*</span></label>
                <input type="number" name="quantity.current" value={formData.quantity.current} onChange={handleInputChange} min="0" className={INP} required />
              </div>
              <div>
                <label className={LBL}>Reserved</label>
                <input type="number" name="quantity.reserved" value={formData.quantity.reserved} onChange={handleInputChange} min="0" className={INP} />
              </div>
              <div>
                <label className={LBL}>Available</label>
                <input type="number" name="quantity.available" value={formData.quantity.available} onChange={handleInputChange} min="0" className={INP_RO} readOnly />
              </div>
              <div>
                <label className={LBL}>Reorder Level</label>
                <input type="number" name="quantity.reorderLevel" value={formData.quantity.reorderLevel} onChange={handleInputChange} min="0" className={INP} />
              </div>
              <div>
                <label className={LBL}>Max Level</label>
                <input type="number" name="quantity.maxLevel" value={formData.quantity.maxLevel} onChange={handleInputChange} min="0" className={INP} />
              </div>
            </div>
          </div>

          {/* Purchase Information */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="ShoppingCart" size={18} />
                Purchase Information
              </h4>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className={LBL}>Purchase Date</label>
                <input type="date" name="purchase.purchaseDate" value={formData.purchase.purchaseDate} onChange={handleInputChange} className={INP} />
              </div>
              <div>
                <label className={LBL}>Supplier Name</label>
                <input type="text" name="purchase.supplierName" value={formData.purchase.supplierName} onChange={handleInputChange} className={INP} />
              </div>
              <div>
                <label className={LBL}>Invoice Number</label>
                <input type="text" name="purchase.invoiceNumber" value={formData.purchase.invoiceNumber} onChange={handleInputChange} className={INP} />
              </div>
              <div>
                <label className={LBL}>Unit Cost <span className="text-red-500 normal-case">*</span></label>
                <input type="number" name="purchase.unitCost" value={formData.purchase.unitCost} onChange={handleInputChange} min="0" step="0.01" className={INP} required />
              </div>
              <div>
                <label className={LBL}>Total Cost</label>
                <input type="number" name="purchase.totalCost" value={formData.purchase.totalCost} onChange={handleInputChange} min="0" step="0.01" className={INP_RO} readOnly />
              </div>
            </div>
          </div>

          {/* Storage Information */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="Thermometer" size={18} />
                Storage Information
              </h4>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className={LBL}>Manufacturing Date</label>
                  <input type="date" name="storage.manufacturingDate" value={formData.storage.manufacturingDate} onChange={handleInputChange} className={INP} />
                </div>
                <div>
                  <label className={LBL}>Expiry Date <span className="text-red-500 normal-case">*</span></label>
                  <input type="date" name="storage.expiryDate" value={formData.storage.expiryDate} onChange={handleInputChange} className={INP} required />
                </div>
                <div>
                  <label className={LBL}>Storage Location</label>
                  <input type="text" name="storage.storageLocation" value={formData.storage.storageLocation} onChange={handleInputChange} className={INP} placeholder="e.g., Shelf A-1, Room 101" />
                </div>
              </div>
              <div>
                <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-3">Storage Conditions</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={LBL}>Temperature</label>
                    <input type="text" name="storage.conditions.temperature" value={formData.storage.conditions.temperature} onChange={handleInputChange} className={INP} placeholder="e.g., 2-8°C" />
                  </div>
                  <div>
                    <label className={LBL}>Humidity</label>
                    <input type="text" name="storage.conditions.humidity" value={formData.storage.conditions.humidity} onChange={handleInputChange} className={INP} placeholder="e.g., <60% RH" />
                  </div>
                  <div className="flex items-center pt-8">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="storage.conditions.lightProtection" checked={formData.storage.conditions.lightProtection} onChange={handleInputChange} className="h-5 w-5 rounded border-2 border-[#2D869C] text-[#2D869C] focus:ring-[#9DC9D4]" />
                      <span className="text-md font-semibold text-gray-600">Light Protection Required</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="IndianRupee" size={18} />
                Pricing Information
              </h4>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={LBL}>Selling Price <span className="text-red-500 normal-case">*</span></label>
                <input type="number" name="pricing.sellingPrice" value={formData.pricing.sellingPrice} onChange={handleInputChange} min="0" step="0.01" className={INP} required />
              </div>
              <div>
                <label className={LBL}>MRP</label>
                <input type="number" name="pricing.mrp" value={formData.pricing.mrp} onChange={handleInputChange} min="0" step="0.01" className={INP} />
              </div>
            </div>
          </div>

          {/* Alert Settings */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <Icon name="Bell" size={18} />
                Alert Settings
              </h4>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'alerts.lowStock', checked: formData.alerts.lowStock, label: 'Low Stock Alert' },
                { name: 'alerts.nearExpiry', checked: formData.alerts.nearExpiry, label: 'Near Expiry Alert' },
                { name: 'alerts.expired', checked: formData.alerts.expired, label: 'Expired Alert' },
              ].map(({ name, checked, label }) => (
                <label key={name} className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-[#2D869C] transition-all cursor-pointer">
                  <input type="checkbox" name={name} checked={checked} onChange={handleInputChange} className="h-5 w-5 rounded border-2 border-[#2D869C] text-[#2D869C] focus:ring-[#9DC9D4]" />
                  <span className="text-md font-semibold text-gray-600">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200">
            <button
              type="button"
              onClick={closeDialogs}
              className="flex items-center gap-2 px-6 py-3 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
            >
              <Icon name="X" size={16} />
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitLoading}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl disabled:cursor-not-allowed transition-all"
            >
              {submitLoading ? (
                <><Icon name="Loader2" size={18} className="animate-spin" />{isEdit ? 'Updating...' : 'Creating...'}</>
              ) : (
                <><Icon name={isEdit ? 'Check' : 'Plus'} size={18} />{isEdit ? 'Update Item' : 'Create Item'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Filters */}
      <div className="bg-gradient-to-r from-[#2D869C]/5 to-[#9DC9D4]/10 p-6 rounded-xl shadow-sm border border-[#2D869C]/20">
        <div className="flex flex-col space-y-4">
          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-[#2D869C] mb-2">
                Search Inventory
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by medicine name, batch number..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent shadow-sm"
                />
                <Icon name="Search" size={18} className="absolute left-3 top-3.5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2D869C] mb-2">
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent shadow-sm"
              >
                <option value="all">All Status</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleAddNew}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Icon name="Plus" size={18} className="mr-2" />
                Add Item
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-[#E99797]/10 border-l-4 border-[#E99797] text-[#E99797] px-6 py-4 rounded-xl shadow-sm flex items-center">
          <Icon name="AlertCircle" size={20} className="mr-3" />
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-[#A4EC90]/10 border-l-4 border-[#A4EC90] text-green-700 px-6 py-4 rounded-xl shadow-sm flex items-center">
          <Icon name="CheckCircle2" size={20} className="mr-3" />
          <span className="font-medium">{success}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
              <tr>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Medicine
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Manufacturer
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Batch No.
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Expiry
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                      Loading inventory items...
                    </div>
                  </td>
                </tr>
              ) : inventoryItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No inventory items found
                  </td>
                </tr>
              ) : (
                inventoryItems.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md font-semibold text-gray-900">
                        {getMedicineName(item)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md font-semibold text-gray-700">
                        {getManufacturerName(item)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md font-semibold text-gray-700">
                        {item.batchNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md font-semibold text-gray-900">
                        {item.quantity?.current || 0}
                        {item.quantity?.reorderLevel && item.quantity.current <= item.quantity.reorderLevel && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E99797]/20 text-red-800">
                            <Icon name="AlertTriangle" size={12} className="mr-1" />
                            Low
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Available: {item.quantity?.available || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md text-gray-700 font-semibold">
                        {item.unit}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md text-gray-700 font-semibold">
                        {item.storage?.expiryDate ? new Date(item.storage.expiryDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${item.status === 'active' ? 'bg-[#A4EC90]/20 text-green-800' :
                          item.status === 'expired' ? 'bg-[#E99797]/20 text-red-800' :
                            item.status === 'damaged' ? 'bg-[#CCB478]/20 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                        }`}>
                        {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-md font-semibold text-gray-700">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleView(item)}
                          className="text-[#2D869C] hover:text-[#2D869C]/80 transition-colors p-1 hover:bg-[#2D869C]/10 rounded-lg"
                          title="View Details"
                        >
                          <Icon name="Eye" size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-[#CCB478] hover:text-[#CCB478]/80 transition-colors p-1 hover:bg-[#CCB478]/10 rounded-lg"
                          title="Edit"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="text-[#E99797] hover:text-[#E99797]/80 transition-colors p-1 hover:bg-[#E99797]/10 rounded-lg"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-r from-gray-50 to-[#2D869C]/5 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-[#2D869C]/30 text-sm font-semibold rounded-xl text-[#2D869C] bg-white hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-[#2D869C]/30 text-sm font-semibold rounded-xl text-[#2D869C] bg-white hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-[#2D869C]">
                  Page <span className="font-bold">{currentPage}</span> of{' '}
                  <span className="font-bold">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-xl shadow-sm space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-2 rounded-xl border border-[#2D869C]/30 bg-white text-sm font-semibold text-[#2D869C] hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Icon name="ChevronLeft" size={18} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-2 rounded-xl border border-[#2D869C]/30 bg-white text-sm font-semibold text-[#2D869C] hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Icon name="ChevronRight" size={18} />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Dialog */}
      {showViewDialog && renderViewDialog()}

      {/* Add/Edit Dialog */}
      {showAddDialog && renderFormDialog(false)}
      {showEditDialog && renderFormDialog(true)}
    </div>
  );
};

export default InventoryManagementTab;